"use client";

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ISMSCommitteeCombobox } from "../components/ui/ISMSCommitteeCombobox";

export function ISMSCommitteeComboboxExample() {
  const [selectedCommittees, setSelectedCommittees] = React.useState<string[]>([]);
  
  // Example of programmatically setting selected committees
  const setExampleCommittees = () => {
    setSelectedCommittees(['committee-1', 'committee-2']);
  };
  
  // Clear all selected committees
  const clearAll = () => {
    setSelectedCommittees([]);
  };
  
  // Handle when a committee is selected
  const handleCommitteeSelect = (committeeId: string) => {
    console.log(`Committee selected: ${committeeId}`);
  };
  
  // Handle when a committee is deselected
  const handleCommitteeDeselect = (committeeId: string) => {
    console.log(`Committee deselected: ${committeeId}`);
  };
  
  // Example of custom committees data (optional)
  const customCommittees = [
    { id: 'committee-1', roleName: 'Security Steering Committee' },
    { id: 'committee-2', roleName: 'Risk Management Committee' },
    { id: 'committee-3', roleName: 'Privacy Committee' },
    { id: 'committee-4', roleName: 'Compliance Committee' },
  ];
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>ISMS Committee Combobox</CardTitle>
          <CardDescription>
            A combobox component for selecting ISMS committees.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Basic Usage</h3>
            <ISMSCommitteeCombobox
              selected={selectedCommittees}
              setSelected={setSelectedCommittees}
              onCommitteeSelect={handleCommitteeSelect}
              onCommitteeDeselect={handleCommitteeDeselect}
              className="w-full"
            />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-sm font-medium">With Custom Committees</h3>
            <ISMSCommitteeCombobox
              selected={selectedCommittees}
              setSelected={setSelectedCommittees}
              committees={customCommittees}
              className="w-full"
              singleSelect={true}
            />
          </div>
          
          <div className="flex space-x-2 pt-2">
            <Button variant="outline" size="sm" onClick={setExampleCommittees}>
              Set Example Committees
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll}>
              Clear All
            </Button>
          </div>
          
          <div className="pt-4">
            <h3 className="text-sm font-medium mb-2">Selected Committees:</h3>
            <div className="space-y-2">
              {selectedCommittees.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedCommittees.map((committeeId) => {
                    const committee = [...customCommittees].find(c => c.id === committeeId);
                    return (
                      <Badge key={committeeId} variant="secondary">
                        {committee ? committee.roleName : committeeId}
                      </Badge>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No committees selected</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
